/**
 * templateCatalog.js — the built-in templates shipped in data/templates/.
 *
 * One list for everything that needs to know which templates are built in: the
 * startup template picker, the copilot's list_templates / create tools, the
 * outdated-template check, and the guard that stops a saved template from
 * taking over a built-in's file name.
 */

export const TEMPLATE_CATALOG = {
  manufacturing: {
    name: 'Manufacturing',
    color: '#28a745',
    subcategories: {
      nmc: {
        name: 'NMC Chemistries',
        processes: [
          {
            id: 'nmc111',
            name: 'NMC111 Cell Manufacturing',
            description: 'LiNi₁/₃Mn₁/₃Co₁/₃O₂ — balanced performance, mature chemistry',
            template: 'nmc111_cell_manufacturing.json',
            csv: 'nmc111_cell_manufacturing.csv',
            chemistry: 'NMC111',
          },
          {
            id: 'nmc622',
            name: 'NMC622 Cell Manufacturing',
            description: 'LiNi₀.₆Mn₀.₂Co₀.₂O₂ — higher energy density, reduced cobalt',
            template: 'nmc622_cell_manufacturing.json',
            csv: 'nmc622_cell_manufacturing.csv',
            chemistry: 'NMC622',
          },
          {
            id: 'nmc811',
            name: 'NMC811 Cell Manufacturing',
            description: 'LiNi₀.₈Mn₀.₁Co₀.₁O₂ — highest energy density, minimal cobalt',
            template: 'nmc811_cell_manufacturing.json',
            csv: 'nmc811_cell_manufacturing.csv',
            chemistry: 'NMC811',
          },
        ],
      },
      lfp: {
        name: 'LFP',
        processes: [
          {
            id: 'lfp',
            name: 'LFP Cell Manufacturing',
            description: 'LiFePO₄ — cobalt-free, long cycle life, high safety',
            template: 'lfp_cell_manufacturing.json',
            csv: 'lfp_cell_manufacturing.csv',
            chemistry: 'LFP',
          },
        ],
      },
    },
  },
  recycling: {
    name: 'Recycling',
    color: '#17a2b8',
    subcategories: {
      braws: {
        name: 'BRAWS',
        processes: [
          {
            id: 'braws',
            name: 'BRAWS Recycling Process',
            description: 'Battery Resource Assessment and Waste Solutions — hydrometallurgical recycling',
            template: 'braws_template.json',
            csv: 'braws_recycling.csv',
            chemistry: 'Li-NMC',
          },
        ],
      },
    },
  },
};

/**
 * BatPaC process templates, one per cathode, written by
 * scripts/build_batpac_templates.py into data/templates/batpac/. The "Design
 * with BatPaC" screen lists them by family. A cathode no dataset covers is
 * scored as a stand-in cathode's powder (`standIn`), and the screen says so.
 */
export const BATPAC_FAMILIES = ['NMC', 'High-nickel', 'LFP and LMFP', 'Manganese-rich', 'LCO'];

export const BATPAC_TEMPLATES = [
  { key: 'nmc111', label: 'NMC111', family: 'NMC' },
  { key: 'nmc532', label: 'NMC532', family: 'NMC' },
  { key: 'nmc622', label: 'NMC622', family: 'NMC' },
  { key: 'nmc811', label: 'NMC811', family: 'High-nickel' },
  { key: 'nmc90', label: 'NMC90', family: 'High-nickel', standIn: 'NMC95' },
  { key: 'nmc95', label: 'NMC95', family: 'High-nickel' },
  { key: 'nca', label: 'NCA', family: 'High-nickel' },
  { key: 'lfp', label: 'LFP', family: 'LFP and LMFP' },
  { key: 'lmfp', label: 'LMFP', family: 'LFP and LMFP', standIn: 'LFP' },
  { key: 'lmo', label: 'LMO', family: 'Manganese-rich' },
  { key: 'lnmo', label: 'LNMO', family: 'Manganese-rich', standIn: 'LMO' },
  { key: 'lmr', label: 'LMR', family: 'Manganese-rich' },
  { key: 'lco', label: 'LCO', family: 'LCO' },
].map((t) => ({
  ...t,
  id: `batpac_${t.key}`,
  name: `${t.label} Cell, BatPaC design`,
  template: `batpac/${t.key}.json`,
  chemistry: t.label,
  category: 'BatPaC',
}));

/**
 * Every built-in template as a flat list, each carrying its category name: the
 * samples, then the BatPaC process templates.
 */
export const BUILTIN_TEMPLATES = [
  ...Object.values(TEMPLATE_CATALOG).flatMap((category) =>
    Object.values(category.subcategories).flatMap((sub) =>
      sub.processes.map((proc) => ({ ...proc, category: category.name })))),
  ...BATPAC_TEMPLATES,
];

/**
 * Find a built-in template by file name ("nmc111_cell_manufacturing.json",
 * "batpac/nmc811.json"), template_id ("nmc111_cell_manufacturing",
 * "batpac_nmc811") or catalog id ("nmc111").
 */
export function findBuiltinTemplate(key) {
  if (!key || typeof key !== 'string') return null;
  const k = key.toLowerCase();
  const stem = k.replace(/\.json$/, '');
  return BUILTIN_TEMPLATES.find((t) =>
    t.template === k || t.template.replace(/\.json$/, '') === stem || t.id === k) || null;
}

/** True when a saved template under `fileName` would shadow a built-in. */
export function isBuiltinTemplateFile(fileName) {
  if (!fileName) return false;
  const f = fileName.toLowerCase();
  return BUILTIN_TEMPLATES.some((t) => t.template === f);
}
